import { useMemo, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import type { DefaultValues, Path } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, CheckCircle2, Download, Info, MinusCircle, Plus, Upload } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ZodSchema } from "zod";
import {
  createNeutralActivitySchema,
  createOperationSchema,
  createProductSchema,
  createUnitSchema,
  type NeutralActivityDto,
  type UnitDto,
} from "@normax-performance/shared-types";
import { useAuth } from "../../app/providers/AuthProvider";
import { catalogApi, type ImportResult } from "../../lib/api-client/endpoints";
import { ApiError } from "../../lib/api-client/client";
import {
  Badge,
  Button,
  CatalogToolbar,
  Input,
  Modal,
  OverflowMenu,
  SortableColumnHeader,
  useSortableRows,
  useToast,
  type OverflowMenuItem,
} from "../../shared-ui/primitives";
import { useCatalogFilter } from "../../shared-ui/hooks/useCatalogFilter";
import type { FieldDescriptor } from "./catalogFormFields";
import { CatalogImportResultModal } from "./CatalogImportResultModal";
import { NormsTab } from "./NormsTab";
import "./MasterDataPage.css";

/** Product and Operation are structurally identical for this slice's CRUD
 * purposes (id, canonicalName, code, active) — one generic tab component
 * drives both, configured per entity below, rather than duplicating the
 * same list/create/edit/activate logic twice. `code` added for business-code
 * fast search — NORMAX_PERFORMANCE_BUSINESS_CODES_FAST_SEARCH_IMPLEMENTATION_REPORT.md §7. */
interface CatalogEntity {
  id: string;
  canonicalName: string;
  code: string | null;
  active: boolean;
}

interface CatalogEntityConfig<TEntity extends { id: string }, TValues extends Record<string, unknown>> {
  queryKey: string;
  list: (params: { includeInactive: boolean }) => Promise<TEntity[]>;
  create: (input: TValues) => Promise<TEntity>;
  update: (id: string, input: Partial<TValues> & { active?: boolean }) => Promise<TEntity>;
  addLabel: string;
  editLabel: string;
  emptyTitle: string;
  fields: FieldDescriptor<TValues>[];
  createSchema: ZodSchema<TValues>;
  /** False for Unit — no `active` column exists on that entity at all. */
  hasActiveToggle: boolean;
  /** Localized 409-conflict override for whichever field can conflict (e.g. `code` for Product/Operation) — undefined when no field has a uniqueness conflict worth a special message (falls through to the raw backend message, same as today). */
  conflictField?: keyof TValues & string;
  conflictMessage?: string;
  searchPlaceholder: string;
  statusLabels?: { all: string; active: string; inactive: string };
  /** Entity-specific empty-state guidance, overriding the shared generic
   * `empty.adminHint`/`empty.directorHint` copy — for an entity where the
   * generic "start using Daily Entry" text is inaccurate or a more helpful,
   * example-driven hint already exists (e.g. Units: "e.g. pcs, kg, m, min").
   * Omit to use the shared generic hint (Products/Operations/Neutral
   * Activities' current behavior, unchanged). */
  emptyHint?: { admin: string; director: string };
  /** Present only for the 4 simple catalogs (Products/Operations/Neutral
   * Activities/Units) — Norms never sets this since it doesn't use
   * CatalogEntityTab at all. Function signatures match the real
   * catalogApi.export/importTemplate/import methods from Task 10. */
  importExport?: {
    export: (params: { includeInactive: boolean }) => Promise<{ blob: Blob; filename: string | null }>;
    importTemplate: () => Promise<{ blob: Blob; filename: string | null }>;
    import: (file: File) => Promise<ImportResult>;
  };
}

type FormState<TEntity> = { mode: "create" } | { mode: "edit"; entity: TEntity } | null;

function CatalogEntityForm<TEntity extends { id: string }, TValues extends Record<string, unknown>>({
  config,
  mode,
  entity,
  onClose,
  onSubmit,
  isPending,
}: {
  config: CatalogEntityConfig<TEntity, TValues>;
  mode: "create" | "edit";
  entity?: TEntity;
  onClose: () => void;
  onSubmit: (input: TValues) => void;
  isPending: boolean;
}) {
  const { t: tCommon } = useTranslation("common");
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TValues>({
    resolver: zodResolver(config.createSchema),
    defaultValues: config.fields.reduce(
      (acc, f) => ({ ...acc, [f.key]: entity ? ((entity as unknown as Record<string, unknown>)[f.key] ?? "") : "" }),
      {} as TValues,
    ) as unknown as DefaultValues<TValues>,
  });

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title={mode === "create" ? config.addLabel : config.editLabel}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {tCommon("cancel")}
          </Button>
          <Button type="submit" form="nx-catalog-entity-form" variant="primary" loading={isPending}>
            <Check size={16} />
            {tCommon("save")}
          </Button>
        </>
      }
    >
      <form
        id="nx-catalog-entity-form"
        onSubmit={handleSubmit((raw) => {
          const values = raw as unknown as TValues;
          // Normalize any blank optional text field ("" from a cleared/never-typed
          // input) to `null` FIRST, uniformly for both create and edit — some
          // fields' zod schema already does this at the resolver level (e.g.
          // Product/Operation's fixed-width `code`, via `fixedWidthNumericCodeField`'s
          // internal transform), others don't (e.g. Neutral Activity's free-text
          // `category`, whose schema only permits null/undefined as *additional*
          // input types via `.nullish()` — it does not transform an already-valid
          // empty string). This step makes both cases converge on the same
          // "blank -> null" contract before the per-field CREATE policy below runs.
          const normalized = { ...values };
          for (const key of Object.keys(normalized) as (keyof TValues)[]) {
            if (normalized[key] === "") {
              normalized[key] = null as TValues[typeof key];
            }
          }

          if (mode === "create") {
            // CREATE: for each field resolved to `null` (blank), either omit the
            // key entirely (default — e.g. Product/Operation's `code`, matching the
            // pre-existing create payload shape with no `code` key at all) or keep
            // it as an explicit `null` when the field descriptor opts in via
            // `blankValue: "null"` (e.g. Neutral Activity's `category`).
            const cleaned = { ...normalized };
            for (const key of Object.keys(cleaned) as (keyof TValues)[]) {
              if (cleaned[key] === null) {
                const field = config.fields.find((f) => f.key === key);
                if (field?.blankValue !== "null") {
                  delete cleaned[key];
                }
              }
            }
            onSubmit(cleaned);
          } else {
            // EDIT: always send every resolved value (including `null`) so clearing
            // a previously-set optional value actually reaches the partial-patch
            // update, which only touches fields it receives.
            onSubmit(normalized);
          }
        })}
      >
        {config.fields.map((f) => (
          <Input
            key={f.key}
            label={f.label}
            required={f.required}
            hint={f.hint}
            inputMode={f.type === "numeric-code" ? "numeric" : undefined}
            error={
              f.errorMessage
                ? f.errorMessage(errors[f.key] as { message?: string } | undefined)
                : (errors[f.key] as { message?: string } | undefined)?.message
            }
            {...register(f.key as Path<TValues>)}
          />
        ))}
      </form>
    </Modal>
  );
}

function CatalogEntityTab<TEntity extends { id: string }, TValues extends Record<string, unknown>>({
  config,
  isAdmin,
}: {
  config: CatalogEntityConfig<TEntity, TValues>;
  isAdmin: boolean;
}) {
  const { t } = useTranslation("masterData");
  const { t: tCommon } = useTranslation("common");
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [formState, setFormState] = useState<FormState<TEntity>>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const listQuery = useQuery({
    queryKey: [config.queryKey, "all"],
    queryFn: () => config.list({ includeInactive: true }),
  });

  function handleError(error: unknown) {
    const isConflict =
      error instanceof ApiError &&
      error.code === "CONFLICT" &&
      config.conflictField !== undefined &&
      error.details?.field === config.conflictField;
    showToast({
      title: t("toast.saveErrorTitle"),
      description: isConflict
        ? (config.conflictMessage ?? (error instanceof ApiError ? error.message : t("toast.saveErrorNoConnection")))
        : error instanceof ApiError
          ? error.message
          : t("toast.saveErrorNoConnection"),
      tone: "danger",
    });
  }

  // Mirrors ReportDetailPage.tsx's `downloadPdf` handler exactly: blob ->
  // object URL -> synthetic <a download> click -> revoke on the next tick
  // so the download has certainly started.
  function triggerDownload(blob: Blob, filename: string | null, fallbackFilename: string) {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename ?? fallbackFilename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }

  // Distinct from `handleError` above — a failed download is not a failed
  // save, and titling its toast "Greška pri čuvanju" would be misleading
  // (whole-branch review, Minor #9).
  function handleDownloadError(error: unknown) {
    showToast({
      title: t("toast.downloadErrorTitle"),
      description: error instanceof ApiError ? error.message : t("toast.saveErrorNoConnection"),
      tone: "danger",
    });
  }

  async function handleExport() {
    if (!config.importExport) return;
    try {
      const { blob, filename } = await config.importExport.export({ includeInactive: true });
      triggerDownload(blob, filename, "export.xlsx");
    } catch (error) {
      handleDownloadError(error);
    }
  }

  async function handleImportTemplate() {
    if (!config.importExport) return;
    try {
      const { blob, filename } = await config.importExport.importTemplate();
      triggerDownload(blob, filename, "sablon.xlsx");
    } catch (error) {
      handleDownloadError(error);
    }
  }

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const input = event.target;
    const file = input.files?.[0];
    // Reset immediately (not only on success) so picking the SAME file twice
    // in a row still fires `onChange` again, even if the import itself fails.
    input.value = "";
    if (!file || !config.importExport) return;
    try {
      const result = await config.importExport.import(file);
      setImportResult(result);
    } catch (error) {
      handleError(error);
    }
  }

  const createMutation = useMutation({
    mutationFn: (input: TValues) => config.create(input),
    onSuccess: () => {
      showToast({ title: t("toast.createSuccess"), tone: "success" });
      void queryClient.invalidateQueries({ queryKey: [config.queryKey] });
      setFormState(null);
    },
    onError: handleError,
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<TValues> & { active?: boolean } }) =>
      config.update(id, input),
    onSuccess: (_data, variables) => {
      const keys = Object.keys(variables.input);
      const isStatusToggleOnly = keys.length === 1 && keys[0] === "active";
      showToast({
        title: isStatusToggleOnly
          ? variables.input.active
            ? t("toast.activateSuccess")
            : t("toast.deactivateSuccess")
          : t("toast.updateSuccess"),
        tone: "success",
      });
      void queryClient.invalidateQueries({ queryKey: [config.queryKey] });
      setFormState(null);
    },
    onError: handleError,
  });

  const entities = listQuery.data ?? [];
  const isEmpty = !listQuery.isLoading && !listQuery.isError && entities.length === 0;
  const isPending = createMutation.isPending || updateMutation.isPending;

  const numericCodeField = config.fields.find((f) => f.type === "numeric-code");

  const filter = useCatalogFilter(entities, {
    searchFields: config.fields.map(
      (f) => (e: TEntity) => (e as unknown as Record<string, unknown>)[f.key] as string | null | undefined,
    ),
    codeOf: numericCodeField
      ? (e: TEntity) => (e as unknown as Record<string, unknown>)[numericCodeField.key] as string | null | undefined
      : undefined,
    statusOf: config.hasActiveToggle ? (e: TEntity) => (e as unknown as { active: boolean }).active : undefined,
  });

  // Default sort: first field ascending (Product/Operation's "code"),
  // null-code rows last — sorting is read-only, so available to DIRECTOR
  // too (NORMAX_PERFORMANCE_FIXED_WIDTH_CODES_SORTABLE_MASTER_DATA_IMPLEMENTATION_REPORT.md
  // §11-12/§22). Codes are compared as plain strings inside the hook, never
  // Number() — fixed-width canonical codes make lexical order == numeric
  // order. Sorting is applied AFTER filtering, over `filter.filtered`, not
  // the raw `entities` list.
  const sortColumns = useMemo(() => {
    const cols: Record<string, (e: TEntity) => string | number | null> = {};
    for (const f of config.fields) {
      cols[f.key] = (e: TEntity) => (e as unknown as Record<string, unknown>)[f.key] as string | number | null;
    }
    if (config.hasActiveToggle) {
      cols.status = (e: TEntity) => ((e as unknown as { active: boolean }).active ? 1 : 0);
    }
    return cols;
  }, [config]);

  const { sorted, sortKey, direction, requestSort } = useSortableRows(filter.filtered, sortColumns, {
    key: config.fields[0]?.key ?? "code",
    direction: "asc",
  });

  return (
    <div className="nx-master-data__tab-panel">
      <CatalogToolbar
        filter={filter}
        searchPlaceholder={config.searchPlaceholder}
        statusLabels={config.hasActiveToggle ? config.statusLabels : undefined}
        actions={
          isAdmin || config.importExport ? (
            <>
              {config.importExport ? (
                <>
                  {/* Export/Template inherit the backend's class-level
                      ADMIN+DIRECTOR read access (Task 9 RBAC) — rendered for
                      BOTH roles, unlike Add/Import below which are
                      ADMIN-only writes. */}
                  <Button variant="secondary" onClick={() => void handleExport()}>
                    <Download size={16} />
                    {t("importExport.export")}
                  </Button>
                  <Button variant="secondary" onClick={() => void handleImportTemplate()}>
                    <Download size={16} />
                    {t("importExport.importTemplate")}
                  </Button>
                </>
              ) : null}
              {isAdmin && config.importExport ? (
                <>
                  <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
                    <Upload size={16} />
                    {t("importExport.import")}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    style={{ display: "none" }}
                    onChange={(e) => void handleFileChange(e)}
                  />
                </>
              ) : null}
              {isAdmin ? (
                <Button variant="primary" onClick={() => setFormState({ mode: "create" })}>
                  <Plus size={16} />
                  {config.addLabel}
                </Button>
              ) : null}
            </>
          ) : undefined
        }
      />

      {importResult ? (
        <CatalogImportResultModal
          result={importResult}
          onClose={() => {
            setImportResult(null);
            void queryClient.invalidateQueries({ queryKey: [config.queryKey] });
          }}
        />
      ) : null}

      {listQuery.isLoading ? (
        <p className="nx-field__hint">{tCommon("loading")}</p>
      ) : listQuery.isError ? (
        <p className="nx-master-data__error">{t("toast.saveErrorNoConnection")}</p>
      ) : isEmpty ? (
        <div className="nx-master-data__empty">
          <p className="nx-master-data__empty-title">{config.emptyTitle}</p>
          {/* The Add action lives once, in the toolbar above — not
              duplicated here, to avoid two identical buttons on an empty tab. */}
          <p>
            {config.emptyHint
              ? isAdmin
                ? config.emptyHint.admin
                : config.emptyHint.director
              : isAdmin
                ? t("empty.adminHint")
                : t("empty.directorHint")}
          </p>
        </div>
      ) : filter.filtered.length === 0 ? (
        // There ARE entities, but the current search/status filter matches
        // none of them — a distinct state from the tab having zero entities
        // at all (the `isEmpty` branch above), so the table's header row
        // never renders over a silently-empty <tbody>.
        <div className="nx-master-data__empty">
          <p className="nx-master-data__empty-title">{t("toolbar.noResults")}</p>
        </div>
      ) : (
        <div className="nx-master-data__table-scroll nx-scroll-table">
          <table className="nx-master-data__table nx-table">
            <thead>
              <tr>
                {config.fields.map((f) => (
                  <SortableColumnHeader
                    key={f.key}
                    label={f.headerLabel ?? f.label}
                    sortKey={f.key}
                    activeSortKey={sortKey}
                    direction={direction}
                    onSort={requestSort}
                  />
                ))}
                {config.hasActiveToggle ? (
                  <SortableColumnHeader
                    label={t("table.status")}
                    sortKey="status"
                    activeSortKey={sortKey}
                    direction={direction}
                    onSort={requestSort}
                  />
                ) : null}
                {/* Actions column is intentionally NOT sortable. */}
                {isAdmin ? <th aria-label={t("table.actions")} className="nx-table__actions-col" /> : null}
              </tr>
            </thead>
            <tbody>
              {sorted.map((e) => {
                const actionItems: OverflowMenuItem[] = [
                  { key: "edit", label: tCommon("edit"), onClick: () => setFormState({ mode: "edit", entity: e }) },
                ];
                if (config.hasActiveToggle) {
                  const isActive = (e as unknown as { active: boolean }).active;
                  actionItems.push({
                    key: "toggle",
                    label: isActive ? tCommon("deactivate") : tCommon("activate"),
                    onClick: () =>
                      updateMutation.mutate({
                        id: e.id,
                        input: { active: !isActive } as Partial<TValues> & { active?: boolean },
                      }),
                  });
                }
                return (
                <tr key={e.id}>
                  {config.fields.map((f) => {
                    const value = (e as unknown as Record<string, unknown>)[f.key] as string | null;
                    return (
                      // Code and name are separate columns — never combined
                      // into one cell (NORMAX_PERFORMANCE_FIXED_WIDTH_CODES_SORTABLE_MASTER_DATA_IMPLEMENTATION_REPORT.md §8-9).
                      // Neutral empty-state dash for ANY field's null/undefined
                      // value (numeric-code's null code, Neutral Activity's null
                      // category, etc.) — never a blank cell, never a fake value.
                      <td key={f.key}>{value ?? "—"}</td>
                    );
                  })}
                  {config.hasActiveToggle ? (
                    <td>
                      <Badge
                        tone={(e as unknown as { active: boolean }).active ? "success" : "neutral"}
                        icon={
                          (e as unknown as { active: boolean }).active ? (
                            <CheckCircle2 size={12} aria-hidden />
                          ) : (
                            <MinusCircle size={12} aria-hidden />
                          )
                        }
                      >
                        {(e as unknown as { active: boolean }).active ? tCommon("active") : tCommon("inactive")}
                      </Badge>
                    </td>
                  ) : null}
                  {isAdmin ? (
                    <td className="nx-table__actions-col">
                      <OverflowMenu items={actionItems} triggerLabel={t("table.actions")} />
                    </td>
                  ) : null}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin && formState ? (
        <CatalogEntityForm
          config={config}
          mode={formState.mode}
          entity={formState.mode === "edit" ? formState.entity : undefined}
          onClose={() => setFormState(null)}
          isPending={isPending}
          onSubmit={(input) => {
            if (formState.mode === "create") {
              createMutation.mutate(input);
            } else {
              updateMutation.mutate({ id: formState.entity.id, input });
            }
          }}
        />
      ) : null}
    </div>
  );
}

type Tab = "products" | "operations" | "neutralActivities" | "units" | "norms";

const VALID_TABS: Tab[] = ["products", "operations", "neutralActivities", "units", "norms"];

function isValidTab(value: string | null): value is Tab {
  return value !== null && (VALID_TABS as string[]).includes(value);
}

/**
 * Operational MVP Slice A — Šifarnici / Master Data (Products + Operations
 * only in this slice — Norms explicitly deferred, see
 * NORMAX_PERFORMANCE_OPERATIONAL_MVP_SLICE_A_IMPLEMENTATION_REPORT.md).
 * Same ADMIN-write/DIRECTOR-read contract as Workers, enforced server-side.
 */
export function MasterDataPage() {
  const { t } = useTranslation("masterData");
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";
  // Norm recommendations RBAC change (2026-10-08): DIRECTOR gets full edit
  // parity with ADMIN on Norms specifically — every OTHER tab
  // (Products/Operations/Neutral Activities/Units) stays ADMIN-only via
  // the existing `isAdmin` flag above, untouched.
  const canEditNorms = user?.role === "ADMIN" || user?.role === "DIRECTOR";
  // Deep link from the Dashboard's recommendation count card —
  // `?tab=norms&recommended=1` opens straight on the filtered Norme tab
  // instead of the Products default. Read once on mount only: the tab
  // switcher's own state (not the URL) drives subsequent navigation, same
  // as every other tabbed page in this app.
  const [searchParams] = useSearchParams();
  const initialTabParam = searchParams.get("tab");
  const [tab, setTab] = useState<Tab>(isValidTab(initialTabParam) ? initialTabParam : "products");
  const initialRecommendedOnly = searchParams.get("recommended") === "1";

  const productsConfig: CatalogEntityConfig<CatalogEntity, { canonicalName: string; code?: string | null }> = {
    queryKey: "products",
    list: catalogApi.products,
    create: catalogApi.createProduct,
    update: catalogApi.updateProduct,
    addLabel: t("addProduct"),
    editLabel: t("editProduct"),
    emptyTitle: t("empty.productsTitle"),
    fields: [
      { key: "code", label: t("field.code"), type: "numeric-code", required: false, errorMessage: (e) => (e ? t("field.productCodeInvalid") : undefined) },
      { key: "canonicalName", label: t("field.canonicalName"), type: "text", required: true },
    ],
    createSchema: createProductSchema,
    hasActiveToggle: true,
    conflictField: "code",
    conflictMessage: t("conflict.productCode"),
    searchPlaceholder: t("toolbar.searchPlaceholder.products"),
    statusLabels: {
      all: t("toolbar.statusLabels.all"),
      active: t("toolbar.statusLabels.active"),
      inactive: t("toolbar.statusLabels.inactive"),
    },
    importExport: {
      export: catalogApi.exportProducts,
      importTemplate: catalogApi.importTemplateProducts,
      import: catalogApi.importProducts,
    },
  };
  const operationsConfig: CatalogEntityConfig<CatalogEntity, { canonicalName: string; code?: string | null }> = {
    queryKey: "operations",
    list: catalogApi.operations,
    create: catalogApi.createOperation,
    update: catalogApi.updateOperation,
    addLabel: t("addOperation"),
    editLabel: t("editOperation"),
    emptyTitle: t("empty.operationsTitle"),
    fields: [
      { key: "code", label: t("field.code"), type: "numeric-code", required: false, errorMessage: (e) => (e ? t("field.operationCodeInvalid") : undefined) },
      { key: "canonicalName", label: t("field.canonicalName"), type: "text", required: true },
    ],
    createSchema: createOperationSchema,
    hasActiveToggle: true,
    conflictField: "code",
    conflictMessage: t("conflict.operationCode"),
    searchPlaceholder: t("toolbar.searchPlaceholder.operations"),
    statusLabels: {
      all: t("toolbar.statusLabels.all"),
      active: t("toolbar.statusLabels.active"),
      inactive: t("toolbar.statusLabels.inactive"),
    },
    importExport: {
      export: catalogApi.exportOperations,
      importTemplate: catalogApi.importTemplateOperations,
      import: catalogApi.importOperations,
    },
  };
  const neutralActivitiesConfig: CatalogEntityConfig<
    NeutralActivityDto,
    { canonicalName: string; category?: string | null }
  > = {
    // "neutral-activities" (hyphenated), NOT "neutralActivities" — must match
    // DailyEntryPage.tsx's `neutralActivitiesQuery`'s literal `["neutral-activities"]`
    // queryKey exactly, so this tab's create/update invalidation (`[config.queryKey]`)
    // actually reaches and refreshes Daily Entry's picker cache too (same
    // mechanism already proven for Products/Operations/Units/Settings).
    queryKey: "neutral-activities",
    list: catalogApi.neutralActivities,
    create: catalogApi.createNeutralActivity,
    update: catalogApi.updateNeutralActivity,
    addLabel: t("neutralActivities.addNeutralActivity"),
    editLabel: t("neutralActivities.editNeutralActivity"),
    emptyTitle: t("neutralActivities.empty.title"),
    fields: [
      {
        key: "canonicalName",
        label: t("neutralActivities.field.canonicalName"),
        headerLabel: t("neutralActivities.table.name"),
        type: "text",
        required: true,
      },
      {
        key: "category",
        label: t("neutralActivities.field.category"),
        headerLabel: t("neutralActivities.table.category"),
        hint: t("neutralActivities.field.categoryHint"),
        type: "text",
        required: false,
        blankValue: "null",
      },
    ],
    createSchema: createNeutralActivitySchema,
    hasActiveToggle: true,
    searchPlaceholder: t("toolbar.searchPlaceholder.neutralActivities"),
    statusLabels: {
      all: t("toolbar.statusLabels.all"),
      active: t("toolbar.statusLabels.active"),
      inactive: t("toolbar.statusLabels.inactive"),
    },
    importExport: {
      export: catalogApi.exportNeutralActivities,
      importTemplate: catalogApi.importTemplateNeutralActivities,
      import: catalogApi.importNeutralActivities,
    },
  };
  const unitsConfig: CatalogEntityConfig<UnitDto, { code: string; name: string }> = {
    queryKey: "units",
    // Unit has no includeInactive concept — the underlying call takes no
    // params; this wrapper intentionally ignores the `includeInactive` param
    // CatalogEntityTab always passes.
    list: () => catalogApi.units(),
    create: catalogApi.createUnit,
    update: catalogApi.updateUnit,
    addLabel: t("units.addUnit"),
    editLabel: t("units.editUnit"),
    emptyTitle: t("units.empty.title"),
    fields: [
      { key: "code", label: t("units.field.code"), headerLabel: t("units.table.code"), type: "text", required: true },
      { key: "name", label: t("units.field.name"), headerLabel: t("units.table.name"), type: "text", required: true },
    ],
    createSchema: createUnitSchema,
    // Unit has no `active` column on the Prisma model at all — this is the
    // ONE entity in this slice with hasActiveToggle: false, proving
    // CatalogEntityTab genuinely suppresses the status column/filter/
    // activate-button rather than rendering a broken toggle for a field
    // that doesn't exist.
    hasActiveToggle: false,
    conflictField: "code",
    conflictMessage: t("conflict.unitCode"),
    searchPlaceholder: t("toolbar.searchPlaceholder.units"),
    // statusLabels intentionally omitted — hasActiveToggle: false means
    // CatalogEntityTab must never read it.
    // Restores the deleted UnitsTab.tsx's own example-driven empty-state
    // guidance (which entity this is feeds Norms, not Daily Entry directly —
    // the shared generic hint text is inaccurate for Units specifically).
    emptyHint: {
      admin: t("units.empty.adminHint"),
      director: t("units.empty.directorHint"),
    },
    importExport: {
      export: catalogApi.exportUnits,
      importTemplate: catalogApi.importTemplateUnits,
      import: catalogApi.importUnits,
    },
  };

  return (
    <div className="nx-master-data">
      <div className="nx-master-data__header">
        <h1 className="nx-master-data__title">{t("title")}</h1>
      </div>

      {!isAdmin ? (
        <div className="nx-master-data__notice">
          <Info size={18} aria-hidden="true" />
          <p>{t("readOnlyNotice")}</p>
        </div>
      ) : null}

      <div className="nx-master-data__tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "products"}
          className={["nx-master-data__tab", tab === "products" ? "nx-master-data__tab--active" : ""]
            .filter(Boolean)
            .join(" ")}
          onClick={() => setTab("products")}
        >
          {t("tabs.products")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "operations"}
          className={["nx-master-data__tab", tab === "operations" ? "nx-master-data__tab--active" : ""]
            .filter(Boolean)
            .join(" ")}
          onClick={() => setTab("operations")}
        >
          {t("tabs.operations")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "neutralActivities"}
          className={["nx-master-data__tab", tab === "neutralActivities" ? "nx-master-data__tab--active" : ""]
            .filter(Boolean)
            .join(" ")}
          onClick={() => setTab("neutralActivities")}
        >
          {t("tabs.neutralActivities")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "units"}
          className={["nx-master-data__tab", tab === "units" ? "nx-master-data__tab--active" : ""]
            .filter(Boolean)
            .join(" ")}
          onClick={() => setTab("units")}
        >
          {t("tabs.units")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "norms"}
          className={["nx-master-data__tab", tab === "norms" ? "nx-master-data__tab--active" : ""]
            .filter(Boolean)
            .join(" ")}
          onClick={() => setTab("norms")}
        >
          {t("tabs.norms")}
        </button>
      </div>

      {tab === "products" ? (
        <CatalogEntityTab config={productsConfig} isAdmin={isAdmin} />
      ) : tab === "operations" ? (
        <CatalogEntityTab config={operationsConfig} isAdmin={isAdmin} />
      ) : tab === "neutralActivities" ? (
        <CatalogEntityTab config={neutralActivitiesConfig} isAdmin={isAdmin} />
      ) : tab === "units" ? (
        <CatalogEntityTab config={unitsConfig} isAdmin={isAdmin} />
      ) : (
        <NormsTab isAdmin={canEditNorms} initialRecommendedOnly={initialRecommendedOnly} />
      )}
    </div>
  );
}
